import { rawAxios } from '@/api/httpClient';
import type { ApiEnvelope } from '@/api/errors';
import type { AuthTokens } from '@/types/auth';
import { tokenStore } from './tokenStore';

// Единственное место обновления access-токена. Single-flight: параллельные 401 ждут один
// refresh. Провал refresh (refresh-токен истёк/невалиден) → чистим сессию и эмитим
// onSessionExpired, на которое подписан AuthProvider (logout + редирект). Транспорт про роутер
// не знает — только событие.

const sessionExpiredListeners = new Set<() => void>();

export function onSessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

function emitSessionExpired(): void {
  for (const listener of sessionExpiredListeners) {
    listener();
  }
}

interface TokenData {
  readonly access_token?: string;
  readonly refresh_token?: string;
  readonly expires_in?: number;
}

let inflight: Promise<string | undefined> | null = null;

async function doRefresh(): Promise<string | undefined> {
  const refresh = tokenStore.getRefresh();
  if (!refresh) {
    tokenStore.clear();
    emitSessionExpired();
    return undefined;
  }
  try {
    // Прямой rawAxios (мимо runner): без auth-заголовка, без рекурсивного refresh, без retry —
    // refresh-токен может быть одноразовым (ротация), повтор небезопасен.
    const response = await rawAxios.post<ApiEnvelope<TokenData>>(
      '/usermanagement/registration/refresh-token',
      new URLSearchParams({ refreshToken: refresh }),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
    );
    const data = response.data?.data;
    if (response.data?.success === false || !data?.access_token || !data.refresh_token) {
      throw new Error('refresh failed');
    }
    const tokens: AuthTokens = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      ttl: data.expires_in,
    };
    tokenStore.setTokens(tokens);
    return tokens.accessToken;
  } catch {
    tokenStore.clear();
    emitSessionExpired();
    return undefined;
  }
}

/** Single-flight обновление токена: конкурентные вызовы получают один и тот же промис. */
export function refreshAccessToken(): Promise<string | undefined> {
  inflight ??= doRefresh().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Восстановление сессии при загрузке приложения по сохранённому refresh-токену. */
export async function restoreSession(): Promise<boolean> {
  if (!tokenStore.hasSession()) {
    return false;
  }
  const token = await refreshAccessToken();
  return token !== undefined;
}
